import React, { useState, useEffect } from 'react';
import NavLinks from '../Navbar/NavLinks';
import { Link } from 'react-router-dom';
import newLogo from '../../assets/robomind-logo.svg';
import { useTranslation } from 'react-i18next';
import { PATH_LANG, PREFIXES, prefixFor, PILLAR_SLUGS } from '../../langPath';

const LANGUAGES = ['en', 'hu', 'de', 'ro'];

const NavBar = () => {
    const { t, i18n } = useTranslation();
    const [top, setTop] = useState(window.scrollY <= 10);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        const scrollHandler = () => setTop(window.scrollY <= 10);
        window.addEventListener('scroll', scrollHandler);
        return () => window.removeEventListener('scroll', scrollHandler);
    }, []);

    const closeMenu = () => setIsOpen(false);

    // Switching language has to CHANGE THE URL, not just the i18n state: /hu and
    // the root are two different prerendered document trees, and <Router
    // basename> is fixed for the lifetime of the page. A full navigation is the
    // only way to cross that boundary and land on the correct static HTML.
    // ro/de have no URL tree of their own yet, so they stay on the root and
    // switch in place, exactly as before.
    const switchLanguage = (lng) => {
        // Persist the explicit choice BEFORE navigating. Without this, switching
        // out of a prefixed tree lands on a URL whose stored preference is still
        // the old language, and the page renders the wrong language.
        try { window.localStorage.setItem('lang', lng); } catch (e) { /* private mode */ }

        // Every language is its own prerendered document tree and <Router
        // basename> is fixed for the lifetime of the page, so crossing a tree
        // boundary requires a real navigation.
        const here = window.location.pathname;
        const from = PATH_LANG || 'en';
        if (lng === from) return;
        const current = PATH_LANG ? PREFIXES[PATH_LANG] : '';
        let bare = current ? (here.slice(current.length) || '/') : here;

        // Pages whose slug is localized cannot simply have their prefix swapped:
        // /de/industrielle-kuenstliche-intelligenz must become
        // /ro/inteligenta-artificiala-industriala, not /ro/industrielle-...
        // (which does not exist and would 404). Map the slug across languages.
        if (bare === PILLAR_SLUGS[from]) bare = PILLAR_SLUGS[lng];

        const target = `${prefixFor(lng)}${bare === '/' ? '' : bare}`;
        window.location.assign(target || '/');
    };

    const LanguageSwitcher = ({ isMobile = false }) => (
        <div className={`flex items-center ${isMobile ? 'space-x-2 mt-6 pt-6 border-t border-gray-200' : 'space-x-1 ml-3'}`}>
            {LANGUAGES.map((lng) => (
                <button
                    key={lng}
                    onClick={() => switchLanguage(lng)}
                    disabled={i18n.language.startsWith(lng)}
                    aria-label={t(`aria.change_lang_${lng}`)}
                    className={`${isMobile ? 'px-3' : 'px-2'} py-1 text-sm rounded-md font-bold transition-colors duration-300 disabled:bg-primary disabled:text-white bg-gray-200 text-gray-700 hover:bg-gray-300`}
                >
                    {lng.toUpperCase()}
                </button>
            ))}
        </div>
    );

    return (
        <nav className={`fixed top-0 w-full z-30 bg-white transition-shadow duration-300 ease-in-out ${!top ? 'shadow-lg' : 'shadow-sm'}`}>
            <div className="flex flex-row justify-between items-center py-2 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center shrink-0">
                    <Link to="/" onClick={closeMenu} aria-label="ROBOMIND Systems - Home">
                        <img src={newLogo} alt="ROBOMIND Systems logo" className="h-12 w-auto" />
                    </Link>
                </div>
                <div className="flex items-center">
                    <div className='hidden xl:flex items-center'>
                        <div className='flex items-center py-3'>
                            <NavLinks />
                        </div>
                        <LanguageSwitcher />
                    </div>
                    <div className="xl:hidden ml-4">
                        <button className="p-2 rounded-lg text-primary" onClick={() => setIsOpen(!isOpen)} aria-label={isOpen ? t('aria.close_menu') : t('aria.open_menu')} aria-expanded={isOpen}>
                            <svg className="h-6 w-6 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                                {isOpen && (
                                <path fillRule="evenodd" clipRule="evenodd" d="M18.278 16.864a1 1 0 0 1-1.414 1.414l-4.829-4.828-4.828 4.828a1 1 0 0 1-1.414-1.414l4.828-4.829-4.828-4.828a1 1 0 0 1 1.414-1.414l4.829 4.828 4.828-4.828a1 1 0 1 1 1.414 1.414l-4.828 4.829 4.828 4.828z" />
                                )}
                                {!isOpen && (
                                <path fillRule="evenodd" d="M4 5h16a1 1 0 0 1 0 2H4a1 1 0 1 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2zm0 6h16a1 1 0 0 1 0 2H4a1 1 0 0 1 0-2z" />
                                )}
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
            <div className={`fixed left-0 w-full max-h-[calc(100vh-4rem)] overflow-y-auto rounded-b-md p-8 bg-white xl:hidden shadow-xl top-16 ${isOpen ? "block" : "hidden"}`}>
                <div className='flex flex-col items-center text-center'>
                    <div className='flex flex-col space-y-2'>
                        <NavLinks isMobile={true} onNavigate={closeMenu} />
                    </div>
                    <LanguageSwitcher isMobile={true} />
                </div>
            </div>
        </nav>
    );
}

export default NavBar;
